import { useEffect } from 'react';
import { compose, filter, findIndex, isNil, map, pathEq, pathOr, propEq, propOr } from 'ramda';
import { Header } from '@fasstech/middle-design-system';
import withMiddleFeatures from '@@viewCommonHoc/withMiddleFeatures';
import defaultLogo from '../../public/images/logo.png';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useQuery } from '@apollo/client';
import { QLayoutQuery } from '../graphql/queries';
import middleWorkflows from '../middle-workflows.js';
import formatExtraProjectNumber from '@@app/extra/formatExtraProjectNumber';
import phoneNumber from '@@app/extra/extraPhoneNumber.mjs';
import getExtraGraphics from '@@app/extra/getExtraGraphics';
import { extraActivePolicy } from '@@app/extra/extraActivePolicy';
import useGetSimulationEffectDateQuery
  from '../../views/simulationEffectDate/client/hooks/useGetSimulationEffectDateQuery.js';

const LOGO_WIDTH = 140;

export const useBreadcrumb = (middleFeatures) => {
  const {
    data,
    refetch
  } = useQuery(QLayoutQuery);
  const { context } = useProcess();
  const currentViewId = propOr(null, 'viewId', context);
  const currentOfferCode = propOr(null, 'offerCode', context);
  const creatorUserRoles = propOr([], 'creatorUserRoles', context);
  const currentNumberPricings = pathOr(1, ['layout', 'numberPricings'], data);
  const projectNumber = pathOr(null, ['layout', 'projectNumber'], data);
  const { getSimulationEffectDate } = useGetSimulationEffectDateQuery();

  useEffect(() => {
    refetch();
  }, [currentViewId]);

  const contractOnGoingValue = extraActivePolicy(getSimulationEffectDate);
  const displayActivePolicy = isNil(contractOnGoingValue)
    ? propOr(false, 'PROCESSDEVENTE_DISPLAY_ACTIVE_POLICY', middleFeatures)
    : contractOnGoingValue;
  const displayGamme = propOr(false, 'PROCESSDEVENTE_DISPLAY_GAMME', middleFeatures);
  const companyName = pathOr('', ['layout', 'companyName'], data);
  const companyTooltip = pathOr('', ['layout', 'companyTooltip'], data);

  const views = middleWorkflows(
    currentOfferCode,
    currentNumberPricings,
    displayActivePolicy,
    displayGamme,
    creatorUserRoles.includes('BROKER')
  );
  const currentSubstepIndex = findIndex(propEq('viewId', currentViewId), views);

  return {
    currentSubstepIndex,
    views,
    projectNumber,
    companyName,
    companyTooltip
  };
};

export default withMiddleFeatures(({
  middleFeatures,
  getMiddleText,
  getMiddleImageSrc
}) => {

  const {
    projectNumber,
    currentSubstepIndex,
    views,
    companyName,
    companyTooltip
  } =
    useBreadcrumb(middleFeatures);

  const truncateCompanyName = (name) => {
    if (!name) return '';
    return name.length > 30 ? name.slice(0, 30) + '…' : name;
  };

  const {
    logo,
    classNames
  } = getExtraGraphics();
  const headerSteps = [
    {
      label: 'Simulation',
      selected: pathEq([currentSubstepIndex, 'group'], '1', views)
    },
    {
      label: 'Souscription',
      selected: pathEq([currentSubstepIndex, 'group'], '2', views)
    }
  ];
  const headerSubsteps = views[currentSubstepIndex]
    ? compose(
      map((view) => ({
        label: view.label,
        selected: view === views[currentSubstepIndex]
      })),
      filter((view) => view.group === views[currentSubstepIndex].group)
    )(views)
    : [];

  const group1Substeps = views.filter(propEq('group', '1'));

  const headerProgress = views[currentSubstepIndex]
    ? views[currentSubstepIndex].group === '1'
      ? ((currentSubstepIndex + 1) / headerSubsteps.length) * 100
      : ((currentSubstepIndex - group1Substeps.length + 1) / headerSubsteps.length) * 100
    : 0;

  return (
    <Header
      key={logo}
      src={
        logo ||
        getMiddleImageSrc(
          'GENERAL_GRAPHIC_CLIENT_LOGO',
          {
            width: LOGO_WIDTH,
            mime: 'png'
          },
          defaultLogo
        )
      }
      classNames={{ logo: classNames }}
      phoneNumber={phoneNumber()}
      steps={headerSteps}
      riskLabel={getMiddleText('GENERAL_RISQUE')}
      projectLabel={`Référence projet ${formatExtraProjectNumber(projectNumber)}`}
      substeps={headerSubsteps}
      progress={headerProgress}
      companyName={truncateCompanyName(companyName)}
      companyTooltip={companyTooltip}
    />
  );
});
